// Service management page for Admin role
// Full CRUD operations for services: Create, Read, Update, Archive/Restore
// Features: Search, color picker, template count, archive management
// Services are automatically sorted alphabetically by name
// CHANGE: Removed display_order functionality - services now sort alphabetically
// CHANGE: Harmonized design with SiteManagement and UserManagement for consistent UX
// CHANGE: Replaced inline buttons with DropdownMenu component (kebab menu)
// CHANGE: Replaced native confirm() with ConfirmationDialog component
// CHANGE: Added SearchBar component for better UX
// CHANGE: Implemented separate archived view with back button
// UPDATED: Mobile header now has white background with bottom border separator matching bottom nav styling
// CHANGE: Moved "Nouveau service" button to header
// CHANGE: Updated margins, title size, and borders for consistency
// CHANGE: Refactored to use reusable Modal component instead of inline modal
// CHANGE: Added read-only mode for director role - can view all services but cannot create/modify/archive
// CHANGE: Harmonized with UserManagement page - simplified title to "Services" with counter, moved "Archivés" button to header, removed white search wrapper and separate counter block
// CHANGE: Added hover-to-show behavior for kebab menu - menu now appears only on row hover, matching UserManagement UX
// CHANGE: Added mobile responsive card view with ResponsiveDataView and ResponsiveDataCard
// CHANGE: Added ServiceDetailModal for viewing service details in read-only mode on mobile
// CHANGE: Added ReadOnlyBanner to inform users they are in consultation mode on mobile
// UPDATED: Implemented server-side pagination with infinite scroll for better performance
// UPDATED: Added skeleton loading states for improved perceived performance
// UX: Added progress indicator showing "X chargés sur Y" for infinite scroll
// UPDATED: Replaced SearchBar with ConsolidatedControlBar to show totalCount from server for consistency across all management pages

import { useState, useEffect, useMemo } from 'react';
import { Plus, Edit2, Archive, ArchiveRestore, ArrowLeft, ClipboardList } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/useToast';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import { usePaginatedData } from '../../hooks/usePaginatedData';
import { supabase } from '../../lib/supabase';
import {
  createService,
  updateService,
  archiveService,
  restoreService,
  getServiceTemplateCount,
  SERVICE_COLORS,
  getServiceColorClass,
  type Service
} from '../../lib/services';
import { DropdownMenu } from '../ui/DropdownMenu';
import type { DropdownMenuItem } from '../ui/DropdownMenu';
import { ConfirmationDialog } from '../ui/ConfirmationDialog';
import { ConsolidatedControlBar } from '../ui/ConsolidatedControlBar';
import { MobileSearchFilters } from '../ui/MobileSearchFilters';
import { Modal } from '../ui/Modal';
import { ResponsiveDataView } from '../ui/ResponsiveDataView';
import { ResponsiveDataCard } from '../ui/ResponsiveDataCard';
import { ReadOnlyBanner } from '../ui/ReadOnlyBanner';
import { PageHeader } from '../ui/PageHeader';
import { EmptyState } from '../ui/EmptyState';
import { BouncingDotsLoader } from '../ui/BouncingDotsLoader';

export function ServiceManagement() {
  const { user } = useAuth();
  const { isMobile, isDesktop } = useResponsiveLayout();
  const { showToast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [templateCounts, setTemplateCounts] = useState<Record<string, number>>({});

  // Check if current user is director or on mobile (read-only mode)
  const isReadOnly = user?.role === 'director' || isMobile;

  // Confirmation dialog states
  const [archivingService, setArchivingService] = useState<Service | null>(null);
  const [restoringService, setRestoringService] = useState<Service | null>(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [formColor, setFormColor] = useState(SERVICE_COLORS[0].value);

  // Build filters object for server-side filtering
  const filters = useMemo(() => {
    return {
      is_active: !showArchived,
    };
  }, [showArchived]);

  // Use paginated data hook for services
  const {
    items: services,
    isLoading,
    isLoadingMore,
    hasMore,
    loadMore,
    refresh,
    totalCount,
  } = usePaginatedData<Service>({
    supabase,
    table: 'services',
    pageSize: 20,
    orderBy: { column: 'name', ascending: true },
    filters,
    searchColumn: 'name',
    searchTerm,
  });

  // Load template counts separately
  useEffect(() => {
    loadTemplateCounts();
  }, [services]);

  const loadTemplateCounts = async () => {
    const counts: Record<string, number> = {};
    for (const service of services) {
      counts[service.id] = await getServiceTemplateCount(service.id);
    }
    setTemplateCounts(counts);
  };

  const handleOpenCreateModal = () => {
    setEditingService(null);
    setFormName('');
    setFormColor(SERVICE_COLORS[0].value);
    setShowFormModal(true);
  };

  const handleOpenEditModal = (service: Service) => {
    setEditingService(service);
    setFormName(service.name);
    setFormColor(service.color);
    setShowFormModal(true);
  };

  const handleCloseModal = () => {
    setShowFormModal(false);
    setEditingService(null);
    setFormName('');
    setFormColor(SERVICE_COLORS[0].value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formName.trim()) {
      showToast('Le nom du service est requis', 'error');
      return;
    }

    if (editingService) {
      // Update existing service
      const result = await updateService(editingService.id, {
        name: formName.trim(),
        color: formColor
      });

      if (result.success) {
        showToast('Service mis à jour avec succès', 'success');
        handleCloseModal();
        refresh();
      } else {
        showToast(result.error || 'Erreur lors de la mise à jour', 'error');
      }
    } else {
      // Create new service
      const result = await createService(formName.trim(), formColor);

      if (result.success) {
        showToast('Service créé avec succès', 'success');
        handleCloseModal();
        refresh();
      } else {
        showToast(result.error || 'Erreur lors de la création', 'error');
      }
    }
  };

  const handleArchiveService = (service: Service) => {
    setArchivingService(service);
  };

  const performArchive = async () => {
    if (!archivingService) return;

    const result = await archiveService(archivingService.id);
    if (result.success) {
      setArchivingService(null);
      showToast('Service archivé avec succès', 'success');
      refresh();
    } else {
      showToast(result.error || 'Erreur lors de l\'archivage', 'error');
      setArchivingService(null);
    }
  };

  const handleRestoreService = (service: Service) => {
    setRestoringService(service);
  };

  const performRestore = async () => {
    if (!restoringService) return;

    const result = await restoreService(restoringService.id);
    if (result.success) {
      setRestoringService(null);
      showToast('Service restauré avec succès', 'success');
      refresh();
    } else {
      showToast(result.error || 'Erreur lors de la restauration', 'error');
      setRestoringService(null);
    }
  };

  const getDropdownItems = (service: Service): DropdownMenuItem[] => {
    // Read-only mode for directors - no actions available
    if (isReadOnly) {
      return [];
    }

    if (showArchived) {
      return [
        {
          label: 'Restaurer',
          icon: <ArchiveRestore size={16} />,
          onClick: () => handleRestoreService(service),
        },
      ];
    }

    return [
      {
        label: 'Modifier',
        icon: <Edit2 size={16} />,
        onClick: () => handleOpenEditModal(service),
      },
      {
        label: 'Archiver',
        icon: <Archive size={16} />,
        onClick: () => handleArchiveService(service),
        variant: 'danger' as const,
      },
    ];
  };

  const hasActiveSearch = searchTerm.length > 0;

  const renderServiceCard = (service: Service) => {
    return (
      <ResponsiveDataCard
        primaryLabel={service.name}
        badge={{
          label: service.name,
          colorClass: getServiceColorClass(service.color)
        }}
        metadata={[
          {
            icon: <ClipboardList size={16} className="text-gray-400" />,
            value: `${templateCounts[service.id] || 0} mission${(templateCounts[service.id] || 0) !== 1 ? 's' : ''} associée${(templateCounts[service.id] || 0) !== 1 ? 's' : ''}`
          }
        ]}
      />
    );
  };

  if (!user) return null;

  return (
    <div className="space-y-6">
      {/* Read-only banner for mobile */}
      {isMobile && <ReadOnlyBanner />}

      {/* Header */}
      {showArchived ? (
        <div className={isMobile ? 'bg-white border-b border-gray-200 -mx-4 px-4 pb-4' : ''}>
          <button
            onClick={() => setShowArchived(false)}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 font-medium transition-colors mb-4"
          >
            <ArrowLeft size={16} />
            <span>Retour</span>
          </button>
          <PageHeader title="Services archivés" />
        </div>
      ) : (
        <PageHeader title="Services" isMobile={isMobile}>
          {!isReadOnly && (
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowArchived(true)}
                className="flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-700 font-medium px-4 py-2.5 rounded-lg transition-all duration-200 border border-gray-200 shadow-sm hover:shadow-md"
              >
                <Archive size={18} />
                <span>Archivés</span>
              </button>
              <button
                onClick={handleOpenCreateModal}
                className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white font-semibold px-5 py-2.5 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
              >
                <Plus size={18} />
                <span>Créer</span>
              </button>
            </div>
          )}
        </PageHeader>
      )}

      {/* Search Bar */}
      {!showArchived && (
        <>
          {isDesktop && (
            <ConsolidatedControlBar
              searchValue={searchTerm}
              onSearchChange={setSearchTerm}
              searchPlaceholder="Rechercher un service..."
              itemCount={totalCount}
              itemLabel="service"
              showSort={false}
            />
          )}
          {isMobile && (
            <MobileSearchFilters
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              searchPlaceholder="Rechercher un service..."
              showFilterButton={false}
            />
          )}
        </>
      )}

      {/* Services table/cards */}
      {services.length === 0 && !isLoading ? (
        <EmptyState
          message={showArchived ? 'Aucun service archivé' : hasActiveSearch ? 'Aucun service ne correspond à votre recherche' : 'Aucun service trouvé'}
          actionLabel={hasActiveSearch && !showArchived ? 'Effacer la recherche' : undefined}
          onAction={hasActiveSearch && !showArchived ? () => setSearchTerm('') : undefined}
        />
      ) : (
        <ResponsiveDataView
          items={services}
          isDesktop={isDesktop}
          cardRenderer={renderServiceCard}
          isLoading={isLoading}
          isLoadingMore={isLoadingMore}
          hasMore={hasMore}
          onLoadMore={loadMore}
          tableSkeletonComponent={
            <div className="flex items-center justify-center min-h-[400px]">
              <BouncingDotsLoader size="lg" />
            </div>
          }
          tableComponent={
            <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Service
                      </th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Missions associées
                      </th>
                      <th className="hidden md:table-cell text-right px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider w-20">
                        ACTIONS
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {services.map((service) => (
                      <tr key={service.id} className="hover:bg-gray-50 transition-colors group">
                        <td className="px-6 py-4">
                          <span
                            className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold border ${getServiceColorClass(
                              service.color
                            )}`}
                          >
                            {service.name}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {templateCounts[service.id] || 0} mission{templateCounts[service.id] !== 1 ? 's' : ''}
                        </td>
                        <td className="hidden md:table-cell px-6 py-4 text-sm text-right">
                          <div className="flex items-center justify-end">
                            <DropdownMenu items={getDropdownItems(service)} align="right" />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          }
        />
      )}

      {/* Form Modal */}
      <Modal
        isOpen={showFormModal}
        onClose={handleCloseModal}
        title={editingService ? 'Modifier le service' : 'Nouveau service'}
        size="md"
      >
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nom du service *
            </label>
            <input
              type="text"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Ex: Accueil, Maintenance..."
              required
            />
          </div>

          {/* Color */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Couleur
            </label>
            <div className="grid grid-cols-4 gap-2">
              {SERVICE_COLORS.map((color) => (
                <button
                  key={color.value}
                  type="button"
                  onClick={() => setFormColor(color.value)}
                  className={`px-3 py-2 rounded-lg text-xs font-semibold border-2 transition-all ${
                    formColor === color.value
                      ? 'border-gray-900 ring-2 ring-gray-300'
                      : 'border-transparent'
                  } ${color.class}`}
                >
                  {color.label}
                </button>
              ))}
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={handleCloseModal}
              className="flex-1 px-4 py-2 border border-gray-100 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium transition-colors"
            >
              {editingService ? 'Mettre à jour' : 'Créer'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Archive Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={!!archivingService}
        title="Confirmer l'archivage"
        message={`Êtes-vous sûr de vouloir archiver le service "${archivingService?.name}" ? Il sera déplacé dans les services archivés et ne sera plus accessible.`}
        confirmLabel="Confirmer"
        cancelLabel="Annuler"
        onConfirm={performArchive}
        onCancel={() => setArchivingService(null)}
        variant="warning"
      />

      {/* Restore Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={!!restoringService}
        title="Confirmer la restauration"
        message={`Êtes-vous sûr de vouloir restaurer le service "${restoringService?.name}" ? Il sera réactivé et à nouveau accessible.`}
        confirmLabel="Confirmer"
        cancelLabel="Annuler"
        onConfirm={performRestore}
        onCancel={() => setRestoringService(null)}
        variant="info"
      />
    </div>
  );
}
